import React, { useState, useMemo, useCallback } from 'react'
import { TooltipRenderProps } from 'react-joyride'
import { Box, IconButton, Paper, Menu, MenuItem, Tooltip } from '@mui/material'
import CircleIcon from '@mui/icons-material/Circle'
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import {
  TutorialStep as ImportedTutorialStep,
  ExtendedHelpers as ImportedExtendedHelpers,
} from '../../../tour/types'
import { useModalContext } from '../../../contexts/ModalProvider'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import {
  cleanupAllTourElements,
  cleanupUnnecessaryDependencies,
} from '../../../tour/stepManagement/dependencyManager'

const MAX_DOTS = 999

const circleBtnSx = () => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: '1px solid',
  borderColor: 'primary.main',
  color: 'primary.main',
  fontSize: '1.2rem',
  '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
  '&.Mui-disabled': { borderColor: 'action.disabled', color: 'action.disabled', opacity: 0.5 },
})

interface StandardStepTooltipProps extends TooltipRenderProps {
  allOriginalSteps: readonly ImportedTutorialStep[]
  compatibleJoyrideHelpers: ImportedExtendedHelpers | null
  jumpTargetRefFromController: React.MutableRefObject<number | null>
  setStepIndexFromController: (index: number) => void
}

const StandardStepTooltip: React.FC<StandardStepTooltipProps> = ({
  step,
  index,
  size,
  closeProps,
  primaryProps,
  backProps,
  tooltipProps,
  allOriginalSteps,
  compatibleJoyrideHelpers,
  jumpTargetRefFromController,
  setStepIndexFromController,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const modalContext = useModalContext()
  const { setActiveTab } = useApplicationSuiteContext()

  const currentStepData = step as ImportedTutorialStep

  const sectionData = useMemo(() => {
    const stepsBySection = new Map<string, { steps: ImportedTutorialStep[]; startIndex: number }>()
    let currentIndex = 0

    allOriginalSteps.forEach((step, i) => {
      const section = step.section || 'Untitled Section'
      if (!stepsBySection.has(section)) {
        stepsBySection.set(section, { steps: [], startIndex: currentIndex })
      }
      stepsBySection.get(section)!.steps.push(step)
      currentIndex++
    })

    const currentStep = allOriginalSteps[index]
    const currentSection = currentStep?.section || 'Untitled Section'
    const currentSectionData = stepsBySection.get(currentSection)

    let currentSectionStartIndex = 0
    for (const [section, data] of stepsBySection.entries()) {
      if (section === currentSection) {
        currentSectionStartIndex = data.startIndex
        break
      }
      currentSectionStartIndex += data.steps.length
    }

    const currentSectionStepIndex = index - currentSectionStartIndex

    return {
      stepsBySection,
      currentSection,
      currentSectionData,
      currentSectionStartIndex,
      currentSectionStepIndex,
      sectionsArray: Array.from(stepsBySection.entries()),
    }
  }, [allOriginalSteps, index])

  const dotIndices = useMemo(() => {
    const { currentSectionData, currentSectionStartIndex, currentSectionStepIndex } = sectionData
    if (!currentSectionData) return []

    const sectionStepCount = currentSectionData.steps.length

    if (sectionStepCount <= MAX_DOTS) {
      return [...Array(sectionStepCount).keys()].map((i) => currentSectionStartIndex + i)
    } else {
      const half = Math.floor(MAX_DOTS / 2)
      const start = Math.min(
        Math.max(0, currentSectionStepIndex - half),
        sectionStepCount - MAX_DOTS
      )
      return [...Array(MAX_DOTS).keys()].map((i) => currentSectionStartIndex + start + i)
    }
  }, [sectionData])

  const getFirstStepIndexForSection = useCallback(
    (targetSection: string): number => {
      let stepIndex = 0
      for (const step of allOriginalSteps) {
        if (step.section === targetSection) {
          return stepIndex
        }
        stepIndex++
      }
      return 0
    },
    [allOriginalSteps]
  )

  const renderFooter = !(currentStepData as any).hideFooter

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          maxWidth: dotIndices.length < 6 ? 380 : 520,
          width: '100%',
          borderRadius: 2,
          backgroundColor: '#2D2D2D',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.5)',
        }}
        data-tour-element="true"
        {...tooltipProps}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {renderFooter && (
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ mr: 1 }}
              title="Go to section"
              data-tour-element="true"
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          )}

          <Box component="h3" sx={{ m: 0, flexGrow: 1, fontSize: '1.1rem', textAlign: 'center' }}>
            {React.isValidElement(currentStepData.title)
              ? currentStepData.title
              : String(currentStepData.title || '')}
          </Box>

          <IconButton
            {...closeProps}
            size="small"
            title="Close tour"
            data-tour-element="true"
            onClick={async (e) => {
              if (closeProps.onClick) {
                closeProps.onClick(e)
              }

              try {
                await cleanupAllTourElements({
                  modalContext,
                  setActiveTab,
                })
              } catch (error) {
                console.error('Error during manual tour cleanup:', error)
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2, fontSize: '0.9rem' }}>
          {React.isValidElement(currentStepData.content)
            ? currentStepData.content
            : String(currentStepData.content || '')}
        </Box>

        {renderFooter && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              px: 1,
            }}
          >
            {index > 0 ? (
              <IconButton
                {...backProps}
                sx={circleBtnSx()}
                title="Previous step"
                data-tour-element="true"
              >
                <ArrowBackIosNewIcon fontSize="inherit" />
              </IconButton>
            ) : (
              <Box sx={{ width: 24, height: 24 }} />
            )}

            {dotIndices.length > 1 && (
              <Box
                sx={{ display: 'flex', gap: 1, mx: 2, flexWrap: 'wrap', justifyContent: 'center' }}
              >
                {dotIndices.map((i) => {
                  const stepData = allOriginalSteps[i]
                  const stepTitle = typeof stepData?.title === 'string' ? stepData.title : ''
                  return (
                    <Tooltip
                      title={stepTitle}
                      key={i}
                      arrow
                      PopperProps={{
                        sx: {
                          zIndex: 210000,
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          setAnchorEl(null)
                          jumpTargetRefFromController.current = i
                          setStepIndexFromController(i)
                        }}
                        sx={{ p: 0.75 }}
                        data-tour-element="true"
                      >
                        {i === index ? (
                          <CircleIcon fontSize="inherit" color="primary" />
                        ) : (
                          <CircleOutlinedIcon fontSize="inherit" sx={{ color: 'grey.500' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )
                })}
              </Box>
            )}

            <IconButton
              {...primaryProps}
              sx={circleBtnSx()}
              title={index === size - 1 ? 'Finish tour' : 'Next step'}
              data-tour-element="true"
            >
              <ArrowForwardIosIcon fontSize="inherit" />
            </IconButton>
          </Box>
        )}
      </Paper>

      {renderFooter && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          MenuListProps={{ dense: true }}
          sx={{ zIndex: 200000 }}
          slotProps={{
            paper: {
              sx: { zIndex: 200000 },
            },
          }}
          data-tour-element="true"
        >
          {sectionData.sectionsArray
            .filter(([sectionName]) => sectionName !== 'Review')
            .map(([sectionName, sectionInfo]) => {
              const firstStepIndex = getFirstStepIndexForSection(sectionName)
              const isCurrentSection = sectionName === sectionData.currentSection

              return (
                <MenuItem
                  key={sectionName}
                  selected={isCurrentSection}
                  onClick={async () => {
                    setAnchorEl(null)

                    if (!isCurrentSection && compatibleJoyrideHelpers) {
                      try {
                        await cleanupUnnecessaryDependencies(
                          sectionInfo.steps,
                          allOriginalSteps,
                          index,
                          compatibleJoyrideHelpers,
                          modalContext,
                          undefined, // showcaseContext
                          true // isJumpNavigation
                        )
                      } catch (error) {
                        console.error('Error during dependency cleanup:', error)
                      }
                    }

                    jumpTargetRefFromController.current = firstStepIndex
                    setStepIndexFromController(firstStepIndex)
                  }}
                  data-tour-element="true"
                >
                  {sectionName}
                </MenuItem>
              )
            })}
        </Menu>
      )}
    </>
  )
}

export default StandardStepTooltip
